/**
 * An invite, claim or shared-report link's token never reaches Sentry events or PostHog page
 * URLs (AC-12, FU-191).
 *
 * The raw token is the whole credential for `/invite/[token]` — whoever holds it can accept the
 * invitation — and likewise for `/claim/[token]` (a free-scan claim link: one-time, no expiry
 * while pending, whoever holds it can take ownership of the project) and `/report/[token]` (a
 * shared white-label report: bearer read access until revoked; a `/report/[token]/methodology`
 * sub-page keeps its trailing segment). Each rides in the page URL, so every telemetry pipeline
 * that records "where the user was" would otherwise copy it out: PostHog's `$current_url` / `$pathname` / `$referrer`
 * and their `$initial_*` / `$session_entry_*` twins (the first pageview AFTER accepting still
 * carries it as referrer and initial URL), autocaptured link hrefs (`/auth/login?next=/invite/…`),
 * and Sentry's request URL, transaction name, navigation/fetch/xhr breadcrumbs, span
 * descriptions and trace contexts.
 *
 * Rather than enumerating those fields (a new SDK field would silently leak), every string in the
 * event — at any depth — has the token segment of any of these routes rewritten to the literal
 * `[token]`, whether the path's slashes are raw, percent-encoded (`?next=%2Finvite%2F…`) or
 * double-encoded. A claim token in a `claim=<uuid>` query value is rewritten the same way.
 * Strings with no such route are returned unchanged.
 *
 * PostHog also keeps the first-touch URL in its own persistence (`$initial_person_info`, which
 * posthog-js writes to the `ph_*_posthog` cookie and localStorage) and sends it, outside any
 * event and so outside `before_send`, as `person_properties.$initial_current_url` in every
 * `/flags` request — measured in tests/e2e/invite-token-telemetry.spec.ts. posthog-js sets that
 * record during `capture()`, immediately before `before_send` runs, so the PostHog hook scrubs
 * the persistence stores there too (and once at `loaded`, for a record an earlier visit left).
 *
 * Fail closed: if scrubbing throws, the event is dropped (`null`) — never forwarded as-is — and
 * a store that can't be scrubbed has its first-touch record removed.
 *
 * Wired in at `instrumentation-client.ts` (PostHog `before_send` + `loaded`) and the three
 * `Sentry.init` sites (`beforeSend` + `beforeSendTransaction`).
 */
import type { BeforeSendFn, CaptureResult } from 'posthog-js'

export const INVITE_TOKEN_PLACEHOLDER = '[token]'

/** A path separator: raw, percent-encoded, or double percent-encoded. */
const SEP = '(?:/|%2F|%252F)'

/**
 * The characters that end a token segment (the inside of a regex class). `[` and `]` are in it so
 * an already scrubbed `/invite/[token]` is left alone, and `%` so an encoded `?`/`#`/`&` ends it.
 */
const SEGMENT_END_CHARS = `/?#&%\\s"'<>\\\\;,()\\[\\]`

/**
 * Group 1 is the route prefix up to and including the slash before the token; the token is the
 * next run of characters that can't end a path segment.
 */
const INVITE_TOKEN_SEGMENT = new RegExp(
  `(${SEP}(?:invite|api${SEP}invitations${SEP}preview)${SEP})[^${SEGMENT_END_CHARS}]+`,
  'gi'
)

/**
 * Claim and report tokens are always `crypto.randomUUID()` (claim-link/route.ts, reports/share/
 * route.ts), so unlike an invite token they are matched by that shape only, and only when a
 * segment end follows. `/claim/` and `/report/` also appear in real non-secret paths — Sentry
 * stack-frame filenames (`app:///api/projects/claim/route.js`, `…/[id]/report/page.tsx`) and
 * third-party URLs (`forrester.com/report/the-state-of-ai/…`) — which must keep their grouping
 * and analytics value. An already scrubbed `[token]` is not UUID-shaped, so this stays idempotent.
 */
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const AT_SEGMENT_END = `(?=$|[${SEGMENT_END_CHARS}])`
const UUID_TOKEN_SEGMENT = new RegExp(
  `(${SEP}(?:claim|report)${SEP})${UUID}${AT_SEGMENT_END}`,
  'gi'
)

/**
 * The claim token also rides as a query value — the claim page's sign-in CTA
 * (`/auth/login?claim=<uuid>`), then `/auth/callback` and `/auth/complete` — including inside an
 * encoded `next=` (`%3Fclaim%3D…`, double-encoded `%253Fclaim%253D…`).
 */
const CLAIM_QUERY_VALUE = new RegExp(
  `((?:^|[?&]|%3F|%26|%253F|%2526)claim(?:=|%3D|%253D))${UUID}${AT_SEGMENT_END}`,
  'gi'
)

export function scrubInviteTokenInString(value: string): string {
  const withPlaceholder = (_match: string, prefix: string) => `${prefix}${INVITE_TOKEN_PLACEHOLDER}`
  return value
    .replace(INVITE_TOKEN_SEGMENT, withPlaceholder)
    .replace(UUID_TOKEN_SEGMENT, withPlaceholder)
    .replace(CLAIM_QUERY_VALUE, withPlaceholder)
}

interface ScrubState {
  seen: WeakSet<object>
  changed: boolean
}

function scrubString(value: string, state: ScrubState): string {
  const scrubbed = scrubInviteTokenInString(value)
  if (scrubbed !== value) state.changed = true
  return scrubbed
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/** Rewrites every string reachable from `value` in place; returns the (possibly new) value. */
function scrubDeep(value: unknown, state: ScrubState): unknown {
  if (typeof value === 'string') return scrubString(value, state)
  if (typeof value !== 'object' || value === null) return value
  // A URL instance serialises to its href — scrub that string rather than skip the object.
  if (typeof URL !== 'undefined' && value instanceof URL) {
    return scrubString(value.href, state)
  }
  if (state.seen.has(value)) return value
  state.seen.add(value)

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubDeep(value[i], state)
    }
    return value
  }

  if (!isPlainObject(value)) return value

  for (const key of Object.keys(value)) {
    const scrubbed = scrubDeep(value[key], state)
    const scrubbedKey = scrubString(key, state)
    if (scrubbedKey !== key) {
      delete value[key]
    }
    value[scrubbedKey] = scrubbed
  }
  return value
}

/** Fail closed: a scrubber that throws drops the event instead of forwarding the original. */
function scrubEvent<T extends object>(event: T): T | null {
  try {
    scrubDeep(event, { seen: new WeakSet(), changed: false })
    return event
  } catch {
    return null
  }
}

/** Sentry `beforeSend` / `beforeSendTransaction`: the event with every invite token rewritten. */
export function scrubSentryEvent<T extends object>(event: T): T | null {
  return scrubEvent(event)
}

/** The event half of the PostHog hook: every invite token rewritten; `null` stays dropped. */
export function scrubPostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null
  return scrubEvent(event)
}

/** The slice of posthog-js's `PostHogPersistence` this module touches. */
interface PostHogStore {
  props: Record<string, unknown>
  save(): void
  unregister(prop: string): void
}

/** The slice of the posthog-js instance this module touches. */
interface PostHogStores {
  persistence?: PostHogStore
  sessionPersistence?: PostHogStore
}

/** posthog-js's first-touch record `{ r: referrer, u: url }` — the one `/flags` replays. */
const INITIAL_PERSON_INFO = '$initial_person_info'

function scrubStore(store: PostHogStore | undefined): void {
  if (!store) return
  try {
    const state: ScrubState = { seen: new WeakSet(), changed: false }
    scrubDeep(store.props, state)
    // Only a store that held a token is rewritten to cookie/localStorage.
    if (state.changed) store.save()
  } catch {
    try {
      store.unregister(INITIAL_PERSON_INFO)
    } catch {
      // nothing left to try — the event half still drops or scrubs everything it sends
    }
  }
}

/** Rewrites any invite token posthog-js has persisted (cookie, localStorage, sessionStorage). */
export function scrubPostHogStores(instance: PostHogStores): void {
  scrubStore(instance.persistence)
  scrubStore(instance.sessionPersistence)
}

/**
 * PostHog `before_send`: scrubs the persisted first-touch record posthog-js has just written
 * during this `capture()`, then the event itself. `instance` is read at call time, so passing
 * the not-yet-initialised `posthog` singleton to `posthog.init` is fine.
 */
export function createPostHogBeforeSend(instance: PostHogStores): BeforeSendFn {
  return (event) => {
    scrubPostHogStores(instance)
    return scrubPostHogEvent(event)
  }
}
